import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import _map from 'lodash/map';
import _get from 'lodash/get';
import { PropTypes } from 'prop-types';

import OwnerDetails from './OwnerDetails';

import classes from '../ContactDetails.module.scss';
import Tooltip from 'components/Tooltip/Tooltip';
import { featureSelectors } from '@lifesize/nucleus';
import { FEATURE_FLAG_MULTIPLE_MODERATORS } from 'constants/FeatureFlags';

function getAdditionalNumbers(numbersUrl) {
  const numbersWindow = window.open(numbersUrl, 'AdditionalNumbers');

  // Check for falsy value for electron to avoid "Cannot read property 'focus' of undefined"
  if (numbersWindow) {
    numbersWindow.focus(); // if the window/tab is already open, it will receive the focus
  }
}

const ContactDetails = (props) => {
  const { additionalNumbersURL, callDisplayName, contact, hideOwner, isInCall, pstnNumbers } = props;
  const isMultiModeratorEnabled = useSelector(featureSelectors.getFeatureFlag)(FEATURE_FLAG_MULTIPLE_MODERATORS);

  if (!contact) {
    return null;
  }
  return (
    <>
      {!isInCall && (
        <>
          {contact.extension && [
            <dt key="extensionTitle">
              <FormattedMessage id="components.contactDetails.extension" defaultMessage="Extension" />
            </dt>,
            <dd key="contactExtension">{contact.extension}</dd>
          ]}
          {contact.pin && [
            <dt key="passcodeTitle">
              <FormattedMessage id="components.contactDetails.passcode" defaultMessage="Passcode" />
            </dt>,
            <dd key="passcodeData">{contact.pin}</dd>
          ]}
          {pstnNumbers && !!pstnNumbers.length && (
            <div>
              <dt>
                <FormattedMessage id="components.contactDetails.phone" defaultMessage="Phone" />
              </dt>
              {_map(pstnNumbers, (number, index) => (
                <dd className={classes.flexNumber} key={`contactNumber${index}`}>
                  <span>{number.countryName}:</span>
                  <span>{number.phoneNumber}</span>
                </dd>
              ))}
              {additionalNumbersURL && (
                <dd>
                  <a
                    onClick={() => {
                      getAdditionalNumbers(additionalNumbersURL);
                    }}
                    data-testid="open-additional-numbers"
                  >
                    <FormattedMessage
                      id="components.contactDetails.additionalNumbers"
                      defaultMessage="Additional numbers"
                    />
                  </a>
                </dd>
              )}
            </div>
          )}
          {contact.sipUri && [
            <dt key="sipUriTitle">
              <FormattedMessage id="components.contactDetails.videoAddress" defaultMessage="Video address" />
            </dt>,
            <dd key="sipUriData">{contact.sipUri}</dd>
          ]}
          {!hideOwner && <OwnerDetails owner={contact.owner} />}
        </>
      )}
      {isInCall && (
        <>
          <dt key="displayName">
            <FormattedMessage id="components.meetingDetails.displayName" defaultMessage="Name" />
          </dt>
          {callDisplayName.map((name, index) => (
            <dd className={classes.name} key={`displayNameData-${index}`}>
              <span>
                {name.length > 16 && <Tooltip label={name} className={classes.wrapTooltip} fixPosition={true} />}
              </span>
              <span>{name}</span>
            </dd>
          ))}
          <OwnerDetails owner={contact?.owner} />
        </>
      )}
      {isMultiModeratorEnabled ? (
        <>
          <dt key="moderatorTitle">
            <FormattedMessage id="components.contactDetails.moderators.label" defaultMessage="Moderators" />
          </dt>
          {_get(contact, 'moderators', [])
            .filter((moderator) => !!moderator?.name) // remove non-existant moderators
            .sort((a, b) => {
              const aName = a?.name?.toLowerCase();
              const bName = b?.name?.toLowerCase();
              if (aName < bName) {
                return -1;
              }
              if (aName > bName) {
                return 1;
              }
              return 0;
            })
            ?.map((moderator) => (
              <dd key={moderator?.id || moderator?.uuid}>{moderator?.name}</dd>
            ))}
        </>
      ) : (
        [
          <dt key="moderatorTitle">
            <FormattedMessage id="components.contactDetails.moderator" defaultMessage="Moderator" />
          </dt>,
          <dd key="moderatorData">{_get(contact, 'moderator.name')}</dd>
        ]
      )}
      {contact?.lecturer && [
        <dt key="lecturerTitle">
          <FormattedMessage id="components.contactDetails.lecturer" defaultMessage="Lecturer" />
        </dt>,
        <dd key="lecturerData">{contact?.lecturer?.name}</dd>
      ]}
    </>
  );
};

ContactDetails.propTypes = {
  callDisplayName: PropTypes.array,
  contact: PropTypes.object.isRequired,
  additionalNumbersURL: PropTypes.string,
  hideOwner: PropTypes.bool,
  pstnNumbers: PropTypes.array,
  presentationRestrictionEnabled: PropTypes.bool.isRequired
};

export default ContactDetails;
